import React from 'react';
import { useTranslation } from 'react-i18next';
import { Department } from '../types/Company';
import { buildDepartmentTree, DepartmentTreeNode } from '../utils/departmentTree';
import styles from './DepartmentTreeList.module.css';

type DepartmentTreeListProps = {
  departments: Department[];
  saving: boolean;
  canManage?: boolean;
  onEdit: (department: Department) => void;
  onDelete: (departmentId: string) => void;
};

type DepartmentTreeItemProps = {
  node: DepartmentTreeNode;
  parentName?: string;
  saving: boolean;
  canManage: boolean;
  onEdit: (department: Department) => void;
  onDelete: (departmentId: string) => void;
};

const DepartmentTreeItem: React.FC<DepartmentTreeItemProps> = ({
  node,
  parentName,
  saving,
  canManage,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation(['companies', 'admin', 'common']);
  const { department, children } = node;
  const isRoot = !department.parentDepartmentId;
  const childCount = children.length;

  return (
    <li className={styles.treeItem}>
      <div className={styles.treeRow}>
        <div className={styles.treeMain}>
          <span className={`${styles.treeIcon} ${isRoot ? '' : styles.treeIconChild}`} aria-hidden="true">
            {isRoot ? '◆' : '▸'}
          </span>
          <div className={styles.treeText}>
            <span className={styles.treeName}>{department.name}</span>
            <span className={styles.treeMeta}>
              {isRoot
                ? t('rootDepartment', { ns: 'companies' })
                : parentName
                  ? t('underDepartment', { ns: 'companies', name: parentName })
                  : t('parentDepartment', { ns: 'companies' })}
              {childCount > 0 ? ` · ${t('subDepartmentsCount', { ns: 'companies', count: childCount })}` : ''}
            </span>
          </div>
        </div>

        <span className={`${styles.statusBadge} ${department.isActive ? styles.statusActive : styles.statusInactive}`}>
          {department.isActive ? t('active', { ns: 'admin' }) : t('inactive', { ns: 'admin' })}
        </span>

        {canManage ? (
          <div className={styles.rowActions}>
            <button type="button" className="btn btn-secondary" onClick={() => onEdit(department)}>
              {t('edit', { ns: 'common' })}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => onDelete(department.id)} disabled={saving}>
              {t('delete', { ns: 'common' })}
            </button>
          </div>
        ) : (
          <span className={styles.rowActions} />
        )}
      </div>

      {children.length > 0 ? (
        <ul className={styles.treeChildren}>
          {children.map(child => (
            <DepartmentTreeItem
              key={child.department.id}
              node={child}
              parentName={department.name}
              saving={saving}
              canManage={canManage}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};

export const DepartmentTreeList: React.FC<DepartmentTreeListProps> = ({
  departments,
  saving,
  canManage,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation(['companies', 'admin']);
  const tree = React.useMemo(() => buildDepartmentTree(departments), [departments]);

  if (tree.length === 0) {
    return <div className={styles.emptyState}>{t('noDepartmentsFound', { ns: 'companies' })}</div>;
  }

  const canManageDepartmentsResolved = canManage ?? false;

  return (
    <div className={styles.treePanel}>
      <div className={styles.treeHeader}>
        <span>{t('departmentStructure', { ns: 'companies' })}</span>
        <span>{t('status', { ns: 'admin' })}</span>
        <span className={styles.treeHeaderActions}>{canManageDepartmentsResolved ? t('actions', { ns: 'admin' }) : ''}</span>
      </div>

      <ul className={styles.treeRoot}>
        {tree.map(node => (
          <DepartmentTreeItem
            key={node.department.id}
            node={node}
            saving={saving}
            canManage={canManageDepartmentsResolved}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </div>
  );
};

export default DepartmentTreeList;
